/**
 * The values the API actually serialises. CallScribeContactNoteStatus carries no JsonConverter, so
 * it goes over the wire as a number, and NotApplicable is 0 - read from the served dev2 build, not
 * mapped from the enum's names.
 */
export enum CallScribeContactNoteStatus {
  NotApplicable = 0,
  Posted = 1,
  Failed = 2,
}

export interface IContactNote {
  status: CallScribeContactNoteStatus;
  reason: string | null;
}

const BY_NAME: Record<string, CallScribeContactNoteStatus> = {
  posted: CallScribeContactNoteStatus.Posted,
  failed: CallScribeContactNoteStatus.Failed,
  notapplicable: CallScribeContactNoteStatus.NotApplicable,
};

const BY_NUMBER = new Map<number, CallScribeContactNoteStatus>([
  [CallScribeContactNoteStatus.Posted, CallScribeContactNoteStatus.Posted],
  [CallScribeContactNoteStatus.Failed, CallScribeContactNoteStatus.Failed],
  [
    CallScribeContactNoteStatus.NotApplicable,
    CallScribeContactNoteStatus.NotApplicable,
  ],
]);

/**
 * The write-back contract is still being built on the backend half, so the status arrives either as
 * the enum's name or as its integer. Anything else resolves to null and the surface renders no line
 * at all, because a status this code cannot read is not a status it may label.
 */
export const readContactNote = (raw: unknown): IContactNote | null => {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const source = raw as Record<string, unknown>;
  const rawStatus = source.status ?? source.Status;
  let status: CallScribeContactNoteStatus | undefined;
  if (typeof rawStatus === 'number') {
    status = BY_NUMBER.get(rawStatus);
  } else if (typeof rawStatus === 'string') {
    status = BY_NAME[rawStatus.trim().toLowerCase().replace(/[\s_-]/g, '')];
  }
  if (status === undefined) {
    return null;
  }
  const rawReason = source.reason ?? source.Reason;
  const reason = typeof rawReason === 'string' && rawReason.trim() !== ''
    ? rawReason.trim()
    : null;
  return { status, reason };
};

export const CONTACT_NOTE_LABEL: Record<CallScribeContactNoteStatus, string> = {
  [CallScribeContactNoteStatus.Posted]: 'Posted',
  [CallScribeContactNoteStatus.Failed]: 'Failed',
  [CallScribeContactNoteStatus.NotApplicable]: 'Not applicable (no contact)',
};

/**
 * Autotask has no contact-notes entity, so the server sends its own sentence saying the note went
 * to the company. It is shown verbatim rather than being rebuilt here, so one wording change on the
 * server reaches every surface.
 */
export const contactNoteText = (note: IContactNote | null): string => {
  if (note === null) {
    return '';
  }
  if (note.status === CallScribeContactNoteStatus.Failed) {
    return note.reason === null
      ? 'Failed'
      : `Failed — ${note.reason}`;
  }
  if (note.reason !== null) {
    return note.reason;
  }
  return CONTACT_NOTE_LABEL[note.status];
};
